import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {Observable, of} from 'rxjs';
import {environment} from '../../../../environments/environment';
import {Page, Pageable} from '../../../utils/pageable';
import {CacheService} from '../../../utils/services/cache.service';
import {
  AnswerHolderBase,
  ExerciseSessionByPathRequest,
  ExerciseSessionRecreateRequest,
  ExerciseSessionStatus,
  ItemAvailability,
  V3Chapter,
  V3ExamSession,
  V3ExamSessionStatus,
  V3ExamSessionPart,
  V3ExamSessionPartStatus,
  V3ExerciseSession,
  V3ExerciseSessionQuestion,
  V3ExerciseSet,
} from '../../model/v3/cspa';
import {CspaRestV3} from './cspa-rest-v3';

@Injectable({
  providedIn: 'root',
})
export class CspaRestV3Service implements CspaRestV3 {
  constructor(
    private http: HttpClient,
    private cache: CacheService,
  ) {}
  private readonly jsonHeaders = new HttpHeaders({'Content-Type': 'application/json'});

  private peopleUrl(personId: number, path: string = ''): string {
    return `${environment.eventsApiBase}/v1/people/${personId}${path}`;
  }

  private eventsUrl(path: string): string {
    return `${environment.eventsApiBase}/v1${path}`;
  }

  private appendIfDefined(
    params: HttpParams,
    key: string,
    value?: string | number | boolean | null,
  ): HttpParams {
    if (value === undefined || value === null) {
      return params;
    }
    return params.append(key, String(value));
  }

  isOffline(): Observable<boolean> {
    return of(false);
  }

  listExerciseSets(): Observable<V3ExerciseSet[]> {
    return this.cache.cache(
      'cspa/v3/exerciseSets',
      () => this.http.get<V3ExerciseSet[]>(this.eventsUrl('/struct/sets')),
    );
  }

  getChapters(path: string, depth: number): Observable<V3Chapter[]> {
    let params = new HttpParams();
    params = this.appendIfDefined(params, 'depth', depth ?? null);
    return this.cache.cache(
      `cspa/v3/struct/${path}?depth=${depth}`,
      () => this.http.get<V3Chapter[]>(
        this.eventsUrl(`/struct/${encodeURIComponent(path)}`),
        {params},
      ),
    );
  }

  listPersonAvailabilities(
    personId: number,
    path?: string | null,
    depth?: number | null,
  ): Observable<ItemAvailability[]> {
    let params = new HttpParams();
    params = this.appendIfDefined(params, 'path', path ?? null);
    params = this.appendIfDefined(params, 'depth', depth ?? null);
    return this.cache.cache(
      `cspa/v3/availabilities/person/${personId}/${path ?? ''}?depth=${depth ?? ''}`,
      () => this.http.get<ItemAvailability[]>(
        this.peopleUrl(personId, '/exercise-progress-tree'),
        {params},
      ),
    );
  }

  findSessions(
    personId: number,
    pathPrefix?: string | null,
    pageable?: Pageable,
  ): Observable<Page<V3ExerciseSession>> {
    let params = pageable ? pageable.appendParams(new HttpParams()) : new HttpParams();
    params = this.appendIfDefined(params, 'pathPrefix', pathPrefix ?? null);
    return this.http.get<Page<V3ExerciseSession>>(
      this.peopleUrl(personId, '/exercise-sessions'),
      {params},
    );
  }

  createSession(personId: number, request: ExerciseSessionByPathRequest): Observable<V3ExerciseSession> {
    return this.http.post<V3ExerciseSession>(
      this.peopleUrl(personId, '/exercise-sessions'),
      request,
    );
  }

  recreateSession(personId: number, request: ExerciseSessionRecreateRequest): Observable<V3ExerciseSession> {
    const params = new HttpParams().append('recreate', '');
    return this.http.post<V3ExerciseSession>(
      this.peopleUrl(personId, '/exercise-sessions'),
      request,
      {params},
    );
  }

  getSession(personId: number, uuid: string): Observable<V3ExerciseSession> {
    return this.http.get<V3ExerciseSession>(
      this.peopleUrl(personId, `/exercise-sessions/${uuid}`),
    );
  }

  getSessionQuestions(personId: number, uuid: string): Observable<V3ExerciseSessionQuestion[]> {
    return this.http.get<V3ExerciseSessionQuestion[]>(
      this.peopleUrl(personId, `/exercise-sessions/${uuid}/questions`),
    );
  }

  updateSessionStatus(
    personId: number,
    uuid: string,
    status: ExerciseSessionStatus,
  ): Observable<V3ExerciseSession> {
    return this.http.patch<V3ExerciseSession>(
      this.peopleUrl(personId, `/exercise-sessions/${uuid}/status`),
      JSON.stringify(status),
      {headers: this.jsonHeaders},
    );
  }

  submitQuestionAnswer(
    personId: number,
    uuid: string,
    questionNumber: number,
    answer: AnswerHolderBase,
  ): Observable<void> {
    return this.http.post<void>(
      this.peopleUrl(personId, `/exercise-sessions/${uuid}/questions/${questionNumber}/answer`),
      answer,
    );
  }

  findExamSessions(personId: number, pathPrefix?: string | null): Observable<V3ExamSession[]> {
    let params = new HttpParams();
    params = this.appendIfDefined(params, 'pathPrefix', pathPrefix ?? null);
    return this.http.get<V3ExamSession[]>(
      this.peopleUrl(personId, '/exam-sessions'),
      {params},
    );
  }

  createExamSession(personId: number, request: {path: string}): Observable<V3ExamSession> {
    return this.http.post<V3ExamSession>(
      this.peopleUrl(personId, '/exam-sessions'),
      request,
    );
  }

  getExamSession(personId: number, uuid: string): Observable<V3ExamSession> {
    return this.http.get<V3ExamSession>(
      this.peopleUrl(personId, `/exam-sessions/${uuid}`),
    );
  }

  updateExamSessionStatus(
    personId: number,
    uuid: string,
    status: V3ExamSessionStatus,
  ): Observable<V3ExamSession> {
    return this.http.patch<V3ExamSession>(
      this.peopleUrl(personId, `/exam-sessions/${uuid}/status`),
      JSON.stringify(status),
      {headers: this.jsonHeaders},
    );
  }

  findExamSessionsParts(personId: number, uuid: string): Observable<V3ExamSessionPart[]> {
    return this.http.get<V3ExamSessionPart[]>(
      this.peopleUrl(personId, `/exam-sessions/${uuid}/parts`),
    );
  }

  getPartQuestions(personId: number, uuid: string, partNum: number): Observable<V3ExerciseSessionQuestion[]> {
    return this.http.get<V3ExerciseSessionQuestion[]>(
      this.peopleUrl(personId, `/exam-sessions/${uuid}/parts/${partNum}/questions`),
    );
  }

  updatePartStatus(
    personId: number,
    uuid: string,
    partNum: number,
    status: V3ExamSessionPartStatus,
  ): Observable<V3ExamSessionPart> {
    return this.http.patch<V3ExamSessionPart>(
      this.peopleUrl(personId, `/exam-sessions/${uuid}/parts/${partNum}/status`),
      JSON.stringify(status),
      {headers: this.jsonHeaders},
    );
  }

  submitPartQuestionAnswer(
    personId: number,
    uuid: string,
    partNum: number,
    questionNumber: number,
    answer: AnswerHolderBase,
  ): Observable<void> {
    return this.http.put<void>(
      this.peopleUrl(personId, `/exam-sessions/${uuid}/parts/${partNum}/questions/${questionNumber}/answer`),
      answer,
    );
  }
}
